import React from 'react';

import { LoadableLazy } from '@confluence/loadable';

import type { UseAtAGlanceResult } from '../../at-a-glance/useAtAGlance.ts';
import type { BylineContentType } from '../../utils/contentAri.ts';
import type { ConnectionSectionsData } from '../sections/useConnectionSectionsData.ts';

// Rendered only while open, so Rovodal and the Rovo chat config graph stay out of
// the byline's after-paint chunk on the view page until the user asks for them.
// Nothing is lost by unmounting: Rovodal has no exit transition — it returns
// `null` when closed and has no outer `ModalTransition`.
const TeamworkGraphModalLoadable = LoadableLazy({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-TeamworkGraphModal" */ './TeamworkGraphModal.tsx'))
			.TeamworkGraphModal,
});

export type VisualizationWrapperProps = {
	isOpen: boolean;
	onClose: () => void;
	contentId?: string;
	/** Forwarded to the modal, whose canvas centres on the content's ARI. */
	contentType?: BylineContentType;
	/**
	 * Handed to the modal so the panel's one copy of this content's context draws the
	 * full-size graph and the modal's connection sections. The modal used to collect its
	 * own; that was one duplicate fetch of everything per open.
	 */
	connections: ConnectionSectionsData;
	/** Forwarded to the modal only. */
	atAGlance: UseAtAGlanceResult;
	/**
	 * Forwarded to the modal only. The panel's column resolved it, so one
	 * `useConnectorPromoBanner` decides the variant and fires one `viewed` event for both
	 * surfaces; the modal renders this same element in its own pinned footer slot.
	 */
	promoBanner: React.ReactElement | null;
};

/**
 * The panel's lazy join onto the full-size graph modal.
 *
 * Open state lives on the panel so the header button can open the modal. This wrapper only
 * mounts Rovodal while that state is true.
 *
 * This is also the join for the package's one-fetch rule. The modal is a descendant of the
 * still-mounted panel, so anything it fetched for itself would be a second live copy of what the
 * panel already has. Everything it needs — `connections`, `atAGlance`, `promoBanner` — is
 * therefore handed down from the panel's column through here. Do not give the modal a fetching
 * hook of its own, and do not try to solve a duplicate by gating one surface: both are visible in
 * sequence, so a gate just moves the fetch rather than removing it.
 */
export function VisualizationWrapper({
	isOpen,
	onClose,
	contentId,
	contentType,
	connections,
	atAGlance,
	promoBanner,
}: VisualizationWrapperProps): React.JSX.Element | null {
	if (!isOpen) {
		return null;
	}

	return (
		<TeamworkGraphModalLoadable
			isOpen
			onClose={onClose}
			contentId={contentId}
			contentType={contentType}
			connections={connections}
			atAGlance={atAGlance}
			promoBanner={promoBanner}
		/>
	);
}
